import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ShoppingCart, Users, Store, User, ArrowRight } from 'lucide-react';

const NFTSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const howItWorksSteps = [
    {
      title: "Shop the Platform",
      description: "Browse and discover products",
      icon: ShoppingCart,
    },
    {
      title: "Create Community",
      description: "Build or join a community",
      icon: Users,
    },
    {
      title: "Buy & Sell",
      description: "Transaction between members",
      icon: Store,
    },
    {
      title: "Direct to Consumer",
      description: "Purchase directly from producers",
      icon: User,
    }
  ];

  const benefits = [
    {
      title: "Early Liquidity",
      description: "Access funds before harvest, reducing reliance on traditional lending",
      icon: "/images/bar-chart.svg"
    },
    {
      title: "Global Market Access",
      description: "Trade crops as digital assets on international marketplaces",
      icon: "/images/earth.svg"
    },
    {
      title: "Trust & Transparency",
      description: "Blockchain and IoT verification ensure secure transactions",
      icon: "/images/shield.svg"
    },
    {
      title: "Community Driven",
      description: "Create or join a community to buy food together, rather than as individuals.",
      icon: "/images/users-group.svg"
    }
  ];

  return (
    <section id="nft" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
            Agricultural <span className="gradient-text">NFT Minting</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transform your crops into tradeable digital assets with our revolutionary minting system 
            that bridges physical agriculture with blockchain technology
          </p>
        </motion.div>

        {/* Simplified How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-24"
        >
          <h3 className="text-3xl font-bold text-center mb-16">How Foodsply Works</h3>
          
          <div className="flex flex-col md:flex-row justify-center items-start gap-4 md:gap-0 max-w-6xl mx-auto">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="flex flex-col md:flex-row items-center md:items-start relative group">
                <div className="flex flex-col items-center text-center px-4">
                  <div className="w-24 h-24 rounded-full bg-white border-4 border-green-500 flex items-center justify-center mb-6 shadow-sm group-hover:scale-105 transition-transform duration-300">
                    <step.icon size={40} className="text-green-600" />
                  </div>
                  <h4 className="font-bold text-xl mb-3 text-gray-900 h-14 flex items-center justify-center">{step.title}</h4>
                  <p className="text-gray-600 text-base max-w-[200px] leading-relaxed h-20">{step.description}</p>
                </div>
                
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden md:flex items-center justify-center h-24 px-2 lg:px-6">
                    <ArrowRight size={32} className="text-gray-400" />
                  </div>
                )}
                
                {/* Mobile arrow */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="md:hidden my-8 text-gray-400 transform rotate-90">
                    <ArrowRight size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-20"
        >
          <h3 className="text-2xl font-bold text-center mb-10">System Benefits</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                style={{
                  borderTopColor: '#04AD2F',
                  borderLeftColor: '#04AD2F',
                  borderRightColor: '#EF9C00',
                  borderBottomColor: '#EF9C00',
                  borderWidth: '0.5px',
                  borderStyle: 'solid'
                }}
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-foodsply-green to-foodsply-orange flex items-center justify-center mb-4">
                  <img src={benefit.icon} alt="" className="w-6 h-6" style={{ filter: 'brightness(0) invert(1)' }} />
                </div>
                <h4 className="font-bold text-lg mb-2">{benefit.title}</h4>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <button 
            onClick={() => window.location.href = '/waitlist'}
            className="bg-gradient-to-r from-foodsply-green to-foodsply-orange text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all transform hover:scale-105"
          >
            Join Waitlist
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default NFTSection;